import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { ReactComponent as MessageSquare } from "../../../../assects/svg/client_details/MessageSquare.svg";
import { BASE_URL } from "../../../api/url";
import { getUserProfileDetails } from "../../../../services/clientDetails";
import { Button } from "../../../Ui_Components/Button";

interface UserDetailSectionProps {
  userId: number;
  onExpandChange?: (isExpanded: boolean) => void;
  hidePlansButton?: boolean;
}

interface BasicInformation {
  user_id: number;
  name: string;
  DOB: string;
  Age: string;
  City: string;
  Country: string;
  city_origin: string;
  country_origin: string;
  Goal: string;
  Progress: string;
  Gender: string;
  Phone_number: string;
  Email: string;
  food_preference: string;
  profile_picture: string;
}

interface SubscriptionDetails {
  subscription_type: string;
  subscription_period: string;
  subscription_status: string;
  subscription_assigned_status: string;
  purchase_date: string;
  subscription_end_date: string;
}

interface BMICalculate {
  Gender: string;
  Height: string;
  Weight: string;
  BMI: string;
  BMI_category: string;
  target_weight: string;
}

interface UserReport {
  report_title: string;
  report_file: string;
  current_date: string;
}

interface MedicalData {
  condition__medical_id: number;
  condition__medical_conditions: string;
  info: string | null;
  severity: string | null;
  diagnosed_on: string | null;
}

interface UserProfileResponse {
  success: string;
  message: string;
  basic_information: BasicInformation;
  subscription_details: SubscriptionDetails;
  BMI_calculate: BMICalculate;
  food_allergic: any[];
  medical_data: MedicalData[];
  supplements: any[];
  allergies: any[];
  favorite_foods_or_ingredients: string;
  strongly_dislike_or_wish_to_avoid: string;
  earlier_medication: string;
  other_information: {
    travel_frequence: string;
    eat_out_frequence: string;
    alcohol_frequence: string;
    favorite_cuision: string;
    proffesion: string;
    working_hours: string;
    what_help_looking_from_us: string;
  };
  blood_report: string;
  user_reports: UserReport[];
}

interface HeaderData {
  basic_information: BasicInformation;
  subscription_details: SubscriptionDetails;
}

// Global cache for user profile data - persists across component mounts
const userProfileCache = new Map<number, UserProfileResponse>();

// Shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const UserDetailShimmer: React.FC = () => (
  <>
    <style>{shimmerStyles}</style>
    <div className="mx-6 mb-4">
      <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md h-[88px] shimmer"></div>
    </div>
  </>
);

// Chevron icon that rotates smoothly
const ExpandChevron: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transition: "transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
      color: "#8E8E93",
    }}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// =============================================================================
// HEIGHT FORMATTER
// API returns height as a string like "187.96 ft" or "170 cm".
// When unit is "ft", the numeric value is actually in cm — convert it to feet.
// When unit is "cm", display as-is.
// =============================================================================
const formatHeight = (height: string): string => {
  if (!height) return "N/A";

  const parts = height.trim().split(" ");
  const numericValue = parseFloat(parts[0]);
  const unit = parts[1]?.toLowerCase();

  if (isNaN(numericValue)) return height;

  if (unit === "ft") {
    // The API sends cm value but labels it as ft — convert cm → feet & inches
    const totalInches = numericValue / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet} Ft ${inches} In`;
  }

  if (unit === "cm") {
    return `${numericValue} Cm`;
  }

  // Fallback: return raw value
  return height;
};
// =============================================================================

// =============================================================================
// DATE FORMATTER — converts server date string to IST
// API format: "19 December, 2025 03:00 PM"
// Assumes the server sends this in UTC; converts to Asia/Kolkata for display.
// =============================================================================
const formatDateToIST = (dateStr: string): string => {
  if (!dateStr) return "N/A";

  const match = dateStr.match(
    /^(\d{1,2})\s+([A-Za-z]+),\s+(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  );
  if (!match) return dateStr; // fallback: show raw string if format is unexpected

  const [, day, monthName, year, hourStr, minute, meridiem] = match;

  const months = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  const monthIndex = months.indexOf(monthName.toLowerCase());
  if (monthIndex === -1) return dateStr;

  let hour = parseInt(hourStr, 10);
  if (meridiem.toUpperCase() === "PM" && hour !== 12) hour += 12;
  if (meridiem.toUpperCase() === "AM" && hour === 12) hour = 0;

  // Build the date as UTC (server time), then let toLocaleString convert it to IST
  const utcDate = new Date(
    Date.UTC(
      parseInt(year, 10),
      monthIndex,
      parseInt(day, 10),
      hour,
      parseInt(minute, 10),
    ),
  );

  return utcDate.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};
// =============================================================================

const UserDetailSection: React.FC<UserDetailSectionProps> = ({
  userId,
  onExpandChange,
  hidePlansButton = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Read isDeactivated from navigation state so it can be forwarded to PlansPage ──
  const isDeactivated =
    (location.state as { isDeactivated?: boolean } | null)?.isDeactivated ===
    true;

  const [isExpanded, setIsExpanded] = useState(false);
  const [measuredHeight, setMeasuredHeight] = useState<number>(0);
  const [showMessageModal, setShowMessageModal] = useState(false);

  const [selectedCondition, setSelectedCondition] =
    useState<MedicalData | null>(null);

  const [headerData, setHeaderData] = useState<HeaderData | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfileResponse | null>(
    null,
  );

  const [headerLoading, setHeaderLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const isOnPlansPage = location.pathname.includes("/plans");

  // Measure real content height whenever userProfile loads or expand toggles
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      // Use ResizeObserver to get accurate height even after async content settles
      const ro = new ResizeObserver(() => {
        if (contentRef.current) {
          setMeasuredHeight(contentRef.current.scrollHeight);
        }
      });
      ro.observe(contentRef.current);
      // Also set immediately
      setMeasuredHeight(contentRef.current.scrollHeight);
      return () => ro.disconnect();
    }
  }, [isExpanded, userProfile, detailsLoading]);

  const handleToggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    if (onExpandChange) onExpandChange(isExpanded);
  }, [isExpanded, onExpandChange]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchHeaderData = async () => {
      if (!userId) {
        setError("No user ID provided");
        setHeaderLoading(false);
        return;
      }

      const cachedData = userProfileCache.get(userId);
      if (cachedData) {
        setHeaderData({
          basic_information: cachedData.basic_information,
          subscription_details: cachedData.subscription_details,
        });
        setUserProfile(cachedData);
        setHeaderLoading(false);
        return;
      }

      try {
        setHeaderLoading(true);
        setError(null);

        const data: UserProfileResponse = await getUserProfileDetails(userId);

        if (data.success === "1") {
          userProfileCache.set(userId, data);
          setHeaderData({
            basic_information: data.basic_information,
            subscription_details: data.subscription_details,
          });
          setUserProfile(data);
        } else {
          setError(data.message || "Failed to fetch user profile");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user details",
        );
      } finally {
        setHeaderLoading(false);
      }
    };

    fetchHeaderData();
  }, [userId]);

  useEffect(() => {
    if (!headerData || detailsLoading || userProfile) return;
    const fetchDetailedData = async () => {
      try {
        setDetailsLoading(true);
      } catch (err) {
        console.error("Error loading detailed data:", err);
      } finally {
        setDetailsLoading(false);
      }
    };
    const timer = setTimeout(fetchDetailedData, 100);
    return () => clearTimeout(timer);
  }, [headerData, detailsLoading, userProfile]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getUserAvatar = () => {
    const profileData = headerData || userProfile;
    if (profileData?.basic_information?.profile_picture) {
      return (
        <img
          src={`${BASE_URL}${profileData.basic_information.profile_picture}`}
          alt={profileData.basic_information.name}
          className="w-16 h-16 rounded-md object-cover flex-shrink-0"
        />
      );
    }
    const name = profileData?.basic_information?.name || "User";
    const initials = name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <div className="w-16 h-16 rounded-md bg-[#DAF9F9] flex items-center justify-center flex-shrink-0">
        <span className="text-2xl font-semibold text-[#00A9A9]">
          {initials}
        </span>
      </div>
    );
  };

  const handleMessageClick = (condition: MedicalData, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCondition(condition);
    setShowMessageModal(true);
  };

  const handlePlansClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/client/${userId}/plans`, { state: { isDeactivated } });
  };

  const calculateProgress = () => {
    const sub =
      headerData?.subscription_details || userProfile?.subscription_details;
    if (!sub) return 0;
    const parseDate = (s: string) => {
      const [day, mon, year] = s.split(" ");
      return new Date(`${mon} ${day}, ${year}`);
    };
    try {
      const start = parseDate(sub.purchase_date);
      const end = parseDate(sub.subscription_end_date);
      const now = new Date();
      const total = end.getTime() - start.getTime();
      const elapsed = now.getTime() - start.getTime();
      return Math.min(Math.max((elapsed / total) * 100, 0), 100);
    } catch {
      return 0;
    }
  };

  // GET_USER_PROFILE_DETAILS returns subscription_status as "active" | "expired" | "in progress".
  const isExpired = (): boolean => {
    const sub =
      headerData?.subscription_details || userProfile?.subscription_details;
    if (!sub) return false;
    return sub.subscription_status?.toLowerCase() === "expired";
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (headerLoading) return <UserDetailShimmer />;

  if (error || !headerData) {
    return (
      <div className="mx-6 mb-4">
        <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md p-6 min-h-[200px]">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Failed to Load User Details
            </h3>
            <p className="text-sm text-red-500 mb-2">
              {error || "Unknown error occurred"}
            </p>
            <p className="text-xs text-gray-500">User ID: {userId}</p>
            <Button
              variant="primary"
              onClick={() => {
                userProfileCache.delete(userId);
                window.location.reload();
              }}
              className="mt-4"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-6 mb-4">
      <div className="bg-white border-[0.5px] border-[#F3F4F6] rounded-md overflow-hidden">
        {/* ── Header row (always visible) ── */}
        <div
          className="flex items-center justify-between px-2.5 py-2.5 cursor-pointer select-none"
          onClick={handleToggleExpand}
          style={{
            // Subtle teal tint when expanded
            background: isExpanded
              ? "linear-gradient(90deg, #F9FEFE 0%, #ffffff 100%)"
              : "#ffffff",
            transition: "background 400ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div className="flex items-center gap-3">
            {getUserAvatar()}
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-base 3xl:text-lg font-semibold font-inter text-[#1F1F1F]">
                  {headerData.basic_information.name}
                </h2>
                <div className="w-1 h-1 rounded-full bg-[#D9D9D9]" />
                <span className="text-xs 3xl:text-sm font-inter text-[#1F1F1F]">
                  {headerData.basic_information.Goal}
                </span>
                {isExpired() && (
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium font-inter text-[#FF3B30] bg-[#FFDADA]">
                    Expired
                  </span>
                )}
              </div>
              <div className="flex flex-col items-start gap-1 mt-1">
                <span className="text-[10px] 3xl:text-[12px] font-medium font-inter text-[#8E8E93]">
                  Ends on{" "}
                  {formatDateToIST(
                    headerData.subscription_details.subscription_end_date,
                  )}
                </span>
                <div className="w-60 h-[3px] bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${calculateProgress()}%`,
                      backgroundColor: isExpired() ? "#FF3B30" : "#0DB5B5",
                      transition: "width 600ms cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!isOnPlansPage && !hidePlansButton && (
              <Button variant="primary" size="md" onClick={handlePlansClick}>
                Plans
              </Button>
            )}
          </div>
        </div>

        {/* ── Animated expand panel ── */}
        <div
          style={{
            maxHeight: isExpanded ? `${measuredHeight}px` : "0px",
            opacity: isExpanded ? 1 : 0,
            overflow: "hidden",
            // Expanding: height first, then fade. Collapsing: fade first, then height.
            transition: isExpanded
              ? "max-height 480ms cubic-bezier(0.4, 0, 0.2, 1), opacity 320ms cubic-bezier(0.4, 0, 0.2, 1) 80ms"
              : "opacity 220ms cubic-bezier(0.4, 0, 0.2, 1), max-height 380ms cubic-bezier(0.4, 0, 0.2, 1) 60ms",
          }}
        >
          {/* Inner div measured by ResizeObserver */}
          <div ref={contentRef}>
            {detailsLoading && !userProfile ? (
              <div className="px-6 py-8 flex items-center justify-center">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 border-4 border-[#0DB5B5] border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500">
                    Loading detailed information...
                  </p>
                </div>
              </div>
            ) : userProfile ? (
              /* Uniform padding wrapper — all four sides equal */
              <div className="p-3 flex flex-col gap-3">
                {/* Basic information */}
                <div className="px-4 py-4 border-[0.5px] border-[#6DD0CF] rounded-md bg-[#F9FEFE]">
                  <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-3">
                    Basic information
                  </h3>
                  <div className="grid grid-cols-6 gap-6">
                    {[
                      {
                        label: "GENDER",
                        value: userProfile.BMI_calculate.Gender,
                        color: "#1F1F1F",
                      },
                      {
                        label: "HEIGHT",
                        value: formatHeight(userProfile.BMI_calculate.Height),
                        color: "#1F1F1F",
                      },
                      {
                        label: "WEIGHT",
                        value: userProfile.BMI_calculate.Weight,
                        color: "#1F1F1F",
                      },
                      {
                        label: "BMI",
                        value: userProfile.BMI_calculate.BMI,
                        color: "#FF3B30",
                      },
                      {
                        label: "BMI CATEGORY",
                        value: userProfile.BMI_calculate.BMI_category,
                        color: "#00A9A9",
                      },
                      {
                        label: "TARGET WEIGHT",
                        value: userProfile.BMI_calculate.target_weight,
                        color: "#00A9A9",
                      },
                    ].map(({ label, value, color }) => (
                      <div key={label}>
                        <p className="text-[10px] 3xl:text-[12px] font-medium font-inter text-[#646464] mb-1">
                          {label}
                        </p>
                        <p
                          className="text-[12px] 3xl:text-[14px] font-medium font-inter capitalize"
                          style={{ color }}
                        >
                          {value || "N/A"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {userProfile.medical_data?.length > 0 && (
                  <>
                    <div>
                      <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-3">
                        Medical conditions
                      </h3>
                      <div className="grid grid-cols-5 gap-4">
                        {userProfile.medical_data.map((condition) => (
                          <div
                            key={condition.condition__medical_id}
                            className="flex flex-col px-4 py-3 bg-white rounded-md border-[0.5px] border-[#91E0DF]"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] 3xl:text-[14px] font-medium font-inter text-[#1F1F1F]">
                                {condition.condition__medical_conditions}
                              </span>
                              {condition.info && (
                                <MessageSquare
                                  className="w-4 h-4 text-[#E91E63] cursor-pointer hover:text-[#C2185B] transition-colors"
                                  onClick={(e) =>
                                    handleMessageClick(condition, e)
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="border-t border-[#E0E0E0]" />
                  </>
                )}

                {/* Reports */}
                {userProfile.user_reports?.length > 0 && (
                  <div>
                    <h3 className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F] mb-3">
                      Reports
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                      {userProfile.user_reports.map((report, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between px-4 py-3 bg-white rounded-md border-[0.5px] border-[#91E0DF] cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() =>
                            window.open(
                              `${BASE_URL}${report.report_file}`,
                              "_blank",
                            )
                          }
                        >
                          <span className="text-[12px] 3xl:text-[14px] font-medium font-inter text-[#1F1F1F] mb-1">
                            {report.report_title}
                          </span>
                          <span className="text-[12px] 3xl:text-[14px] font-inter text-[#828282]">
                            {new Date(report.current_date).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {showMessageModal && selectedCondition && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowMessageModal(false)}
        >
          <div
            className="bg-white rounded-md shadow-xl max-w-lg 3xl:max-w-xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
                Message — {selectedCondition.condition__medical_conditions}
              </h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="w-5 h-5 text-[#646464]" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-[16px] 3xl:text-[18px] font-inter text-[#8E8E93]">
                {selectedCondition.info ||
                  "No additional information provided."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailSection;
